import constant from 'lodash/constant'
import { badgeVariants } from '@klear/ui/components/badge'
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from '@klear/ui/components/dialog'
import PDFPreview from '@klear/ui/components/pdf-preview'
import ImgPreview from '@klear/ui/components/img-preview'
import { cn } from '@klear/ui/lib/utils'
import {
  type OrderEventFilePreviewBreadcrumbItem,
  type OrderEventFilePreviewContext
} from '@klear/order-event/file-preview-display'
import type { OrderEventDocumentRef } from '@klear/upload/order-event-documents'
import { buildDocumentFileUrl } from '@klear/upload/file-url'

export type FileBadgeListProps = {
  documents?: readonly OrderEventDocumentRef[]
  previewContext: OrderEventFilePreviewContext
  className?: string
  getEventUrl?: (item: OrderEventFilePreviewBreadcrumbItem) => string
}

function isPdf(name: string): boolean {
  return name.toLowerCase().endsWith('.pdf')
}

export function FileBadgeList({
  documents = [],
  previewContext,
  className,
  getEventUrl = constant('')
}: FileBadgeListProps) {
  if (documents.length === 0) return null

  const ordered = [
    ...documents.filter((document) => isPdf(document.name)),
    ...documents.filter((document) => !isPdf(document.name))
  ]

  return (
    <div className={cn('flex flex-col gap-2 items-end', className)}>
      {ordered.map((document) => {
        const downloadUrl = buildDocumentFileUrl(document.documentKey, document.traderId)
        const Preview = isPdf(document.name) ? PDFPreview : ImgPreview

        return (
          <Dialog key={document.id}>
            <DialogTrigger asChild>
              <button
                type="button"
                className={cn(badgeVariants({ variant: 'secondary' }), 'max-w-full cursor-pointer')}
                title={document.name}
              >
                <span className="min-w-0 truncate">{document.name}</span>
              </button>
            </DialogTrigger>
            <DialogContent aria-describedby={`${document.name}-preview`}>
              <DialogTitle className="sr-only">{document.name}</DialogTitle>
              <Preview
                context={previewContext}
                fileUrl={downloadUrl}
                fileName={document.name}
                getEventUrl={getEventUrl}
              />
            </DialogContent>
          </Dialog>
        )
      })}
    </div>
  )
}
